"use client"

import { motion } from "framer-motion"
import { Flame } from "lucide-react"
import { cn, getCzechDaysText } from "@/lib/utils"

type StreakBadgeSize = "sm" | "md" | "lg"

interface StreakBadgeProps {
  streak?: number // For simple usage with just current streak
  currentStreak?: number // For detailed usage with current and longest
  longestStreak?: number
  hasEntries?: boolean // To determine if should show anything
  inThreat?: boolean // New: indicates streak is at risk (haven't logged today)
  size?: StreakBadgeSize
  compact?: boolean
  className?: string
}

const sizeClasses: Record<StreakBadgeSize, { container: string; icon: string; text: string }> = {
  sm: {
    container: "px-2 py-1 gap-1",
    icon: "h-3.5 w-3.5",
    text: "text-xs",
  },
  md: {
    container: "px-3 py-1.5 gap-1.5",
    icon: "h-4 w-4",
    text: "text-sm",
  },
  lg: {
    container: "px-4 py-2 gap-2",
    icon: "h-5 w-5",
    text: "text-base",
  },
}

/**
 * StreakBadge - Duolingo-style streak counter with animated fire emoji
 *
 * Features:
 * - Animated fire icon with pulse effect
 * - Czech pluralization (den/dny/dní)
 * - Gold/orange gradient with glow effect
 * - Multiple size variants
 * - Compact mode for tight spaces
 *
 * @example
 * <StreakBadge streak={7} /> // "🔥 7 dní v řadě"
 * <StreakBadge streak={1} compact /> // "🔥 1"
 */
export function StreakBadge({
  streak,
  currentStreak,
  longestStreak,
  hasEntries,
  inThreat = false,
  size = "md",
  compact = false,
  className
}: StreakBadgeProps) {
  // Support both usage patterns
  const actualStreak = currentStreak !== undefined ? currentStreak : (streak || 0)
  const sizes = sizeClasses[size]

  // Don't render if no streak or no entries yet - but keep placeholder for layout
  if (actualStreak <= 0 || (hasEntries !== undefined && !hasEntries)) {
    return (
      <div
        className={cn(
          "w-24", // Fixed width to match typical streak badge without padding
          className
        )}
        aria-hidden="true"
      />
    )
  }
  const daysText = getCzechDaysText(actualStreak)

  return (
    <motion.div
      initial={{ scale: 0, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      transition={{
        type: "spring",
        stiffness: 260,
        damping: 20
      }}
      className={cn(
        "inline-flex items-center rounded-full",
        inThreat
          ? "bg-gradient-to-r from-yellow-400/20 via-amber-400/25 to-yellow-400/20"
          : "bg-gradient-to-r from-orange-500/10 via-orange-500/15 to-amber-500/10",
        inThreat
          ? "border-2 border-yellow-500/50 animate-pulse"
          : "border border-orange-500/20",
        inThreat
          ? "shadow-md shadow-yellow-500/30"
          : "shadow-sm shadow-orange-500/10",
        sizes.container,
        className
      )}
      title={`${actualStreak} ${daysText} v řadě`}
    >
      {/* Animated fire icon */}
      <motion.div
        animate={{
          scale: [1, 1.2, 1],
          rotate: [0, 5, -5, 0],
        }}
        transition={{
          duration: 2,
          repeat: Infinity,
          ease: "easeInOut",
        }}
      >
        <Flame
          className={cn(
            sizes.icon,
            inThreat
              ? "text-yellow-600 fill-yellow-500/30"
              : "text-orange-500 fill-orange-500/20"
          )}
        />
      </motion.div>

      {/* Streak count */}
      <span className={cn(
        sizes.text,
        "font-bold tabular-nums",
        inThreat
          ? "text-yellow-700 dark:text-yellow-400"
          : "text-orange-600 dark:text-orange-400"
      )}>
        {actualStreak}
      </span>

      {/* Days text (hidden in compact mode) */}
      {!compact && (
        <span className={cn(
          sizes.text,
          "font-semibold",
          inThreat
            ? "text-yellow-700/90 dark:text-yellow-400/90"
            : "text-orange-600/80 dark:text-orange-400/80"
        )}>
          {daysText} v řadě
        </span>
      )}
    </motion.div>
  )
}
